import { createContext, useContext, PropsWithChildren, useEffect, useMemo } from 'react';
import { useStore } from 'zustand';
import { AuthStoreApi, createAuthStore } from '../stores/auth-store';
import { AuthState } from '../types/auth-state';
import { usePlatform } from '@tofu/platform';
import { useFanforgeApi } from '@hotpot-arcade/fanforge-api-client';
import { useFanforgeConfigs } from '@hotpot-arcade/fanforge-core-client';
import { AuthClient, createAuthClient } from '../auth/create-auth';

export const AuthContext = createContext<AuthStoreApi | null | undefined>(null);
let globalAuthStoreApi: AuthStoreApi | null = null;
let globalAuthClient: AuthClient | null = null;

export const FanforgeAuthProvider = ({ children }: PropsWithChildren): React.JSX.Element => {
  const { authenticate: onAuthenticated, client: apiClient } = useFanforgeApi();
  const { platform } = usePlatform();
  const config = useFanforgeConfigs();

  if (!globalAuthClient) {
    globalAuthClient = createAuthClient(config.platformApiUrl);
  }
  if (!globalAuthStoreApi) {
    globalAuthStoreApi = createAuthStore({ platform, onAuthenticated, apiClient, authClient: globalAuthClient });
  }

  useEffect(() => {
    globalAuthStoreApi?.setState({
      onAuthenticated,
      apiClient,
    });
  }, [onAuthenticated, apiClient]);

  return <AuthContext.Provider value={globalAuthStoreApi}>{children}</AuthContext.Provider>;
};

FanforgeAuthProvider.displayName = 'AuthProvider';

export function useAuth<T>(selector: (state: AuthState) => T): T {
  const store = useContext(AuthContext);
  if (!store) {
    throw new Error('useAuth must be used within AuthProvider.');
  }
  return useStore(store, selector);
}
